import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import Echo from 'laravel-echo';
import * as moment from 'moment';
import { interval, lastValueFrom, ReplaySubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

import { BroadcastEventEnum } from '@haleo-frontend/data-access/enums';
import { Program } from '@haleo-frontend/data-access/models';
import { EnvironmentUtils } from '@haleo-frontend/utils';

declare const window: any;

@Injectable({
    providedIn: 'root'
})
export class BroadcastService {

    private echo: any;
    private initiated: boolean;
    private channel: any;
    private listeners: any;

    events: {
        clientInitialized: ReplaySubject<Program> | null
    };

    constructor(private jwtHelperService: JwtHelperService) {
        this.initialize();
    }

    initialize() {
        this.events = {
            clientInitialized: new ReplaySubject<Program>(1)
        };
        this.listeners = {
            clientInitialized: BroadcastEventEnum.clientInitialized
        };
    }

    async init() {

        // Required to make Pusher available for Echo
        window.Pusher = require('pusher-js');

        const token = await this.jwtHelperService.tokenGetter();
        const tokenContent = this.jwtHelperService.decodeToken(token);
        const clientId = tokenContent.sub;

        this.echo = new Echo({
            broadcaster: 'pusher',
            key: EnvironmentUtils.env.broadcasting.key,
            wsHost: EnvironmentUtils.env.broadcasting.host,
            wsPort: EnvironmentUtils.env.broadcasting.port,
            wssPort: EnvironmentUtils.env.broadcasting.port,
            forceTLS: EnvironmentUtils.env.broadcasting.tls,
            disableStats: true,
            authEndpoint: EnvironmentUtils.env.broadcasting.authEndpoint,
            auth: {
                headers: {
                    Authorization: 'Bearer ' + token,
                    Accept: 'application/json'
                }
            }
        });

        this.channel = this.echo.private('client.' + clientId);

        const timeout = moment().add(10, 'second');
        await lastValueFrom(interval(50)
            .pipe(takeWhile(() => !this.channel.subscription.subscribed && moment().isBefore(timeout))));

        this.initiated = this.channel.subscription.subscribed;
    }

    async startListener(event: BroadcastEventEnum) {

        if (!this.initiated) {
            await this.init();
        }

        if (!this.initiated) {
            this.events.clientInitialized?.error('Failed to subscribe');
            this.events.clientInitialized?.complete();
            return;
        }

        if (!this.listeners[event]) {

            this.listeners[event] = this.channel.listen(event, (data: { program: Program }) => {
                this.events.clientInitialized?.next(data.program);
                this.events.clientInitialized?.complete();
            });
        }
    }

    async disconnect() {
        this.echo?.disconnect();
        this.initiated = false;

        // reinitialize replay subject to prevent emitting the previous client's program
        this.initialize();
    }
}
